import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGameStore } from '../store/gameStore';
import { getGame, updateGame, getTeam, addPlayer, fetchGames } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Minus, Save, Clock, User, ArrowUp, ArrowDown, X, ArrowRight, AlertTriangle } from 'lucide-react';
import { cn } from '../lib/utils';
import { getRestDays, getPitchLimit, AGE_GROUP_LIMITS, REST_DAYS } from '../components/shared/PitchCountBadge';
import { buildRestMap, getRestWarning, isRestEnforced } from '../lib/pitcherRest';

const PitcherQueueItem = ({ player, index, total, pitchCount, onUpdatePitchCount, onMoveUp, onMoveDown, onRemove }) => (
  <div
    data-testid={`pitcher-queue-${player.id}`}
    className="bg-white border border-stone-200 rounded-sm p-3 shadow-sm"
  >
    <div className="flex items-center gap-3">
      <div className="flex flex-col gap-0.5">
        <button onClick={() => onMoveUp(index)} disabled={index === 0} className="h-5 w-5 flex items-center justify-center text-stone-400 hover:text-green-700 disabled:opacity-30">
          <ArrowUp size={12} />
        </button>
        <button onClick={() => onMoveDown(index)} disabled={index === total - 1} className="h-5 w-5 flex items-center justify-center text-stone-400 hover:text-green-700 disabled:opacity-30">
          <ArrowDown size={12} />
        </button>
      </div>
      <div className="w-10 h-10 rounded-sm bg-amber-600 text-white flex items-center justify-center font-bold" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
        {player.jersey_number}
      </div>
      <div className="flex-1">
        <p className="font-semibold text-stone-900">{player.name}</p>
        <p className="text-xs text-stone-500">{index === 0 ? 'Current Pitcher' : `#${index + 1} in queue`}</p>
      </div>
      {index === 0 && (
        <div className="flex items-center gap-2 bg-stone-100 rounded-sm p-2">
          <Button variant="ghost" size="sm" onClick={() => onUpdatePitchCount(player.id, -1)} data-testid={`pitch-minus-${player.id}`}>
            <Minus size={14} />
          </Button>
          <div className="text-center min-w-[50px]">
            <p className="text-2xl font-bold" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              {pitchCount || 0}
            </p>
            <p className="text-[10px] text-stone-500 uppercase">Pitches</p>
          </div>
          <Button variant="ghost" size="sm" onClick={() => onUpdatePitchCount(player.id, 1)} data-testid={`pitch-plus-${player.id}`}>
            <Plus size={14} />
          </Button>
        </div>
      )}
      <button onClick={() => onRemove(player.id)} className="w-6 h-6 text-red-500 hover:bg-red-50 rounded flex items-center justify-center">
        <X size={14} />
      </button>
    </div>
  </div>
);

export default function PitchingPage() {
  const navigate = useNavigate();
  const { currentGameId, currentTeamId } = useGameStore();
  const [game, setGame] = useState(null);
  const [team, setTeam] = useState(null);
  const [pitcherQueue, setPitcherQueue] = useState([]);
  const [pitchCounts, setPitchCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newPitcherName, setNewPitcherName] = useState('');
  const [addingNewPitcher, setAddingNewPitcher] = useState(false);
  const [restMap, setRestMap] = useState({});
  const [restWarnDialog, setRestWarnDialog] = useState({ open: false, player: null, warning: '' });

  useEffect(() => {
    if (!currentGameId || !currentTeamId) {
      navigate('/');
      return;
    }
    loadData();
  }, [currentGameId, currentTeamId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [gameData, teamData] = await Promise.all([
        getGame(currentGameId),
        getTeam(currentTeamId)
      ]);
      setGame(gameData);
      setTeam(teamData);

      // Fetch recent games for pitcher rest-day warnings (non-blocking)
      // Skip when team opts out of rest rules (e.g., softball / travel baseball).
      if (isRestEnforced(teamData)) {
        fetchGames(currentTeamId, gameData.season).then(games => {
          setRestMap(buildRestMap(games || [], currentGameId));
        }).catch(() => setRestMap({}));
      } else {
        setRestMap({});
      }
      
      if (gameData.pitcher_queue?.length > 0) {
        const queuePlayers = gameData.pitcher_queue
          .map(id => teamData.players?.find(p => p.id === id))
          .filter(Boolean);
        setPitcherQueue(queuePlayers);
      }
      
      const counts = {};
      gameData.pitching_history?.forEach(entry => {
        counts[entry.pitcher_id] = (counts[entry.pitcher_id] || 0) + entry.pitch_count;
      });
      setPitchCounts(counts);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load game data');
    } finally {
      setLoading(false);
    }
  };

  const handleMoveUp = (index) => {
    if (index <= 0) return;
    const newQueue = [...pitcherQueue];
    [newQueue[index - 1], newQueue[index]] = [newQueue[index], newQueue[index - 1]];
    setPitcherQueue(newQueue);
  };

  const handleMoveDown = (index) => {
    if (index >= pitcherQueue.length - 1) return;
    const newQueue = [...pitcherQueue];
    [newQueue[index], newQueue[index + 1]] = [newQueue[index + 1], newQueue[index]];
    setPitcherQueue(newQueue);
  };

  const handleAddToQueue = (player) => {
    if (pitcherQueue.some(p => p.id === player.id)) {
      toast.error('Already in queue');
      return;
    }
    const warning = getRestWarning(restMap, player.id);
    if (warning) {
      setRestWarnDialog({ open: true, player, warning });
      return;
    }
    setPitcherQueue([...pitcherQueue, player]);
  };

  const confirmAddToQueueAnyway = () => {
    const p = restWarnDialog.player;
    setRestWarnDialog({ open: false, player: null, warning: '' });
    if (p && !pitcherQueue.some(qp => qp.id === p.id)) {
      setPitcherQueue([...pitcherQueue, p]);
    }
  };

  const handleAddNewPitcher = async () => {
    const name = newPitcherName.trim();
    if (!name) return;
    try {
      setAddingNewPitcher(true);
      const newPlayer = await addPlayer(currentTeamId, { name, jersey_number: '', position: 'P', is_pitcher: true });
      const freshTeam = await getTeam(currentTeamId);
      setTeam(freshTeam);
      setPitcherQueue(prev => [...prev, newPlayer]);
      setNewPitcherName('');
      toast.success(`${name} added to roster & queue`);
    } catch {
      toast.error('Failed to add pitcher');
    } finally {
      setAddingNewPitcher(false);
    }
  };

  const handleRemoveFromQueue = (playerId) => {
    setPitcherQueue(pitcherQueue.filter(p => p.id !== playerId));
  };

  const handleUpdatePitchCount = (pitcherId, delta) => {
    setPitchCounts(prev => ({
      ...prev,
      [pitcherId]: Math.max(0, (prev[pitcherId] || 0) + delta)
    }));
  };

  const handleSubstitutePitcher = async () => {
    if (pitcherQueue.length < 2) {
      toast.error('Need at least 2 pitchers in queue to substitute');
      return;
    }

    try {
      setSaving(true);
      const currentPitcher = pitcherQueue[0];
      const currentPitchCount = pitchCounts[currentPitcher.id] || 0;
      
      const newEntry = {
        pitcher_id: currentPitcher.id,
        pitcher_name: currentPitcher.name,
        pitch_count: currentPitchCount,
        innings_start: game?.current_inning || 1,
        innings_end: game?.current_inning || 1
      };

      const pitchingHistory = [...(game?.pitching_history || []), newEntry];
      const newQueue = pitcherQueue.slice(1);
      
      await updateGame(currentGameId, {
        pitching_history: pitchingHistory,
        pitcher_queue: newQueue.map(p => p.id)
      });

      setGame({ ...game, pitching_history: pitchingHistory });
      setPitcherQueue(newQueue);
      setPitchCounts(prev => ({ ...prev, [currentPitcher.id]: 0 }));
      toast.success(`${currentPitcher.name} substituted (${currentPitchCount} pitches)`);
    } catch (error) {
      toast.error('Failed to substitute pitcher');
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await updateGame(currentGameId, {
        pitcher_queue: pitcherQueue.map(p => p.id)
      });
      toast.success('Pitcher queue saved!');
    } catch (error) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const allPitchers = team?.players?.filter(p => p.is_pitcher) || [];
  const availablePitchers = allPitchers.filter(p => !pitcherQueue.some(qp => qp.id === p.id));

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-100">
        <div className="w-12 h-12 border-4 border-green-900 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-100 p-4 md:p-8" data-testid="pitching-page">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-extrabold text-green-900" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              PITCHERS & PITCH COUNTS
            </h1>
            <p className="text-stone-600">
              {game?.is_home ? 'vs' : '@'} {game?.opponent_name} • Inning {game?.current_inning || 1}
            </p>
            <p className="text-xs text-stone-400 mt-1">Tap pitchers to add to queue • Use arrows to reorder</p>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={handleSubstitutePitcher}
              disabled={pitcherQueue.length < 2 || saving}
              variant="outline"
              data-testid="substitute-pitcher-btn"
            >
              Substitute Pitcher
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              data-testid="save-pitching-btn"
              className="bg-green-900 hover:bg-green-800"
            >
              <Save size={16} className="mr-1" />
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Pitcher Queue */}
          <Card className="border-0 shadow-md">
            <CardHeader>
              <CardTitle style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                PITCHER QUEUE ({pitcherQueue.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="min-h-[200px] rounded-sm border-2 border-dashed border-stone-300 p-2">
                {pitcherQueue.length === 0 ? (
                  <div className="flex items-center justify-center h-full text-stone-400 py-8">
                    <div className="text-center">
                      <User size={32} className="mx-auto mb-2 opacity-50" />
                      <p>Tap available pitchers to add</p>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {pitcherQueue.map((player, index) => (
                      <PitcherQueueItem
                        key={player.id}
                        player={player}
                        index={index}
                        total={pitcherQueue.length}
                        pitchCount={pitchCounts[player.id]}
                        onUpdatePitchCount={handleUpdatePitchCount}
                        onMoveUp={handleMoveUp}
                        onMoveDown={handleMoveDown}
                        onRemove={handleRemoveFromQueue}
                      />
                    ))}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Available Pitchers */}
          <Card className="border-0 shadow-md">
            <CardHeader>
              <CardTitle style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                AVAILABLE PITCHERS ({availablePitchers.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {/* Add new pitcher by name */}
              <div className="flex gap-2 mb-3" data-testid="pitching-add-new-row">
                <Input
                  placeholder="+ Type new pitcher name"
                  value={newPitcherName}
                  onChange={(e) => setNewPitcherName(e.target.value)}
                  onKeyDown={async (e) => {
                    if (e.key === 'Enter' && newPitcherName.trim() && !addingNewPitcher) {
                      e.preventDefault();
                      await handleAddNewPitcher();
                    }
                  }}
                  className="h-9 text-sm"
                  data-testid="pitching-new-pitcher-input"
                />
                <Button
                  className="h-9 bg-green-900 hover:bg-green-800 text-white font-bold text-sm px-4"
                  disabled={!newPitcherName.trim() || addingNewPitcher}
                  onClick={handleAddNewPitcher}
                  data-testid="pitching-new-pitcher-add-btn"
                >
                  {addingNewPitcher ? '...' : 'Add'}
                </Button>
              </div>
              <div className="min-h-[200px] rounded-sm border-2 border-dashed border-stone-300 p-2">
                {availablePitchers.length === 0 ? (
                  <div className="flex items-center justify-center h-full text-stone-400 py-8">
                    <p>{allPitchers.length === 0 ? 'No pitchers on roster' : 'All pitchers in queue'}</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {availablePitchers.map((player) => {
                      const warn = getRestWarning(restMap, player.id);
                      return (
                        <div
                          key={player.id}
                          data-testid={`available-pitcher-${player.id}`}
                          className={cn(
                            "border rounded-sm p-3 shadow-sm cursor-pointer transition-all flex items-center gap-3",
                            warn
                              ? "bg-red-50 border-red-300 hover:border-red-500"
                              : "bg-white border-stone-200 hover:border-green-500 hover:bg-green-50"
                          )}
                          onClick={() => handleAddToQueue(player)}
                        >
                          <div className={cn(
                            "w-10 h-10 rounded-sm text-white flex items-center justify-center font-bold",
                            warn ? "bg-red-500" : "bg-stone-400"
                          )} style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                            {player.jersey_number}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold">{player.name}</p>
                            {warn && (
                              <p className="text-xs text-red-700 flex items-center gap-1 mt-0.5" data-testid={`avail-pitcher-rest-warn-${player.id}`}>
                                <AlertTriangle size={10} className="shrink-0" />
                                <span className="truncate">{warn}</span>
                              </p>
                            )}
                          </div>
                          {warn
                            ? <span className="text-[10px] font-bold bg-red-600 text-white px-2 py-0.5 rounded shrink-0">REST</span>
                            : <ArrowRight size={16} className="text-stone-400" />}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Pitcher Appearance Log (from Field & Scoring pages) */}
        <Card className="border-0 shadow-md mt-6">
          <CardHeader>
            <CardTitle style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              <Clock size={18} className="inline mr-2" />
              PITCHER APPEARANCE LOG
            </CardTitle>
          </CardHeader>
          <CardContent>
            {!game?.pitcher_appearance_log?.length && !game?.pitching_history?.length ? (
              <p className="text-center text-stone-500 py-4">No pitcher changes recorded yet. Changes made on the Field and Scoring pages will appear here.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b-2 border-stone-300">
                      <th className="text-left py-2 px-3 text-sm font-semibold text-stone-600">Pitcher</th>
                      <th className="text-center py-2 px-3 text-sm font-semibold text-stone-600">Team</th>
                      <th className="text-center py-2 px-3 text-sm font-semibold text-stone-600">Innings</th>
                      <th className="text-center py-2 px-3 text-sm font-semibold text-stone-600">Pitches</th>
                      <th className="text-center py-2 px-3 text-sm font-semibold text-stone-600">Rest</th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* Show pitcher_appearance_log from field/scoring pages */}
                    {(game?.pitcher_appearance_log || []).map((entry, idx) => (
                      <tr key={`pal-${idx}`} className="border-b border-stone-100">
                        <td className="py-2 px-3 font-medium">{entry.pitcher_name}</td>
                        <td className="py-2 px-3 text-center">
                          <span className={cn('px-2 py-0.5 rounded text-xs font-bold',
                            entry.is_opponent ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'
                          )}>
                            {entry.is_opponent ? 'Opponent' : 'Your Team'}
                          </span>
                        </td>
                        <td className="py-2 px-3 text-center text-stone-600">
                          {entry.inning_entered}{entry.inning_exited && entry.inning_exited !== entry.inning_entered ? `–${entry.inning_exited}` : ''}
                        </td>
                        <td className="py-2 px-3 text-center">
                          <span className={cn('px-3 py-1 rounded-full font-bold text-sm',
                            entry.pitch_count >= 66 ? 'bg-red-100 text-red-700' : entry.pitch_count >= 41 ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'
                          )}>
                            {entry.pitch_count}
                          </span>
                        </td>
                        <td className="py-2 px-3 text-center">
                          <span className={cn('font-bold text-sm', getRestDays(entry.pitch_count) >= 3 ? 'text-red-600' : getRestDays(entry.pitch_count) >= 1 ? 'text-amber-600' : 'text-green-600')}>
                            {getRestDays(entry.pitch_count)} day{getRestDays(entry.pitch_count) !== 1 ? 's' : ''}
                          </span>
                        </td>
                      </tr>
                    ))}
                    {/* Legacy pitching_history entries */}
                    {(game?.pitching_history || []).map((entry, idx) => (
                      <tr key={`ph-${idx}`} className="border-b border-stone-100">
                        <td className="py-2 px-3 font-medium">{entry.pitcher_name}</td>
                        <td className="py-2 px-3 text-center">
                          <span className="bg-green-100 text-green-700 px-2 py-0.5 rounded text-xs font-bold">Your Team</span>
                        </td>
                        <td className="py-2 px-3 text-center text-stone-600">
                          {entry.innings_start}{entry.innings_end && entry.innings_end !== entry.innings_start ? `–${entry.innings_end}` : ''}
                        </td>
                        <td className="py-2 px-3 text-center">
                          <span className="bg-amber-100 text-amber-700 px-3 py-1 rounded-full font-bold text-sm">{entry.pitch_count}</span>
                        </td>
                        <td className="py-2 px-3 text-center">
                          <span className={cn('font-bold text-sm', getRestDays(entry.pitch_count) >= 3 ? 'text-red-600' : getRestDays(entry.pitch_count) >= 1 ? 'text-amber-600' : 'text-green-600')}>
                            {getRestDays(entry.pitch_count)} day{getRestDays(entry.pitch_count) !== 1 ? 's' : ''}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* REST DAY WARNING DIALOG */}
      <Dialog open={restWarnDialog.open} onOpenChange={(o) => { if (!o) setRestWarnDialog({ open: false, player: null, warning: '' }); }}>
        <DialogContent className="max-w-sm" data-testid="pitching-rest-warn-dialog">
          <DialogHeader>
            <DialogTitle className="text-red-700 flex items-center gap-2">
              <AlertTriangle size={18} /> Rest Day Warning
            </DialogTitle>
            <DialogDescription className="text-stone-700 pt-2">
              <span className="font-bold text-stone-900">{restWarnDialog.player?.name}</span>
              <br />
              {restWarnDialog.warning}
              <br /><br />
              Adding them to the queue before the required rest may violate pitch-count rules and risk arm injury.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-2 mt-2">
            <Button
              className="flex-1 bg-stone-200 hover:bg-stone-300 text-stone-900 font-bold"
              onClick={() => setRestWarnDialog({ open: false, player: null, warning: '' })}
              data-testid="pitching-rest-warn-cancel"
            >
              Cancel
            </Button>
            <Button
              className="flex-1 bg-red-600 hover:bg-red-500 text-white font-bold"
              onClick={confirmAddToQueueAnyway}
              data-testid="pitching-rest-warn-confirm"
            >
              Add Anyway
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
